import { type WorkflowField, type WorkflowStep } from '@/api/workflows';

/**
 * Field types eligible as a condition source. Restricted to types whose value
 * has a stable string shape — pickers (single/radio/checkbox-group) and
 * booleans (toggle/checkbox). Free-text and numeric excluded — see
 * PdfEntriesDrawer ConditionModal commentary for the rationale.
 */
export const CONDITION_SOURCE_TYPES = new Set([
  'SingleSelect',
  'RadioGroup',
  'CheckboxGroup',
  'Toggle',
  'Checkbox',
]);

/**
 * Where on the form tree the condition is being authored. Drives the
 * source-field picker scope:
 *
 *  - `pdf` — PDF entries can reference any field across the whole workflow.
 *    Same as the legacy (omitted) shape; used by `PdfEntriesDrawer`.
 *  - `field` — Field-level: fields in PRIOR steps (all eligible ones), PLUS
 *    fields in the SAME step that appear earlier in document order than the
 *    field being authored. Steps after the owning one are out of reach.
 *  - `section` — Section-level: fields in PRIOR steps (all of them) PLUS
 *    fields in the SAME step that live OUTSIDE the section being authored
 *    AND appear in a section earlier than it. Excludes own section's own
 *    fields and any same-step section that comes at-or-after.
 *  - `step` — Step-level: only fields in PRIOR steps. A step's picker shows
 *    nothing for step #1 (no prior steps exist).
 *
 * `field` and `section` share the same cross-step rule and differ only in how
 * the OWNING step is bounded — by document position for `field`, by section
 * index for `section`.
 */
export type ConditionScope =
  | { kind: 'pdf' }
  | {
      kind: 'field';
      /** Step the field-under-edit lives in. */
      ownStepId: string;
      /** Position of the field-under-edit in its step's document order. */
      ownSectionId: string;
      ownRowId: string;
      ownColumnId: string;
    }
  | {
      kind: 'section';
      /** Step the section-under-edit lives in. */
      ownStepId: string;
      /** Id of the section-under-edit (to exclude its own fields). */
      ownSectionId: string;
    }
  | {
      kind: 'step';
      /** Step-under-edit id. Modal filters to steps whose index < this step's. */
      ownStepId: string;
    };

/** Eligible source fields for one step, in document order. */
export type StepWithFields = {
  stepId: string;
  stepTitle: string;
  fields: WorkflowField[];
};

/**
 * Every field that may be used as a condition source for `scope`, grouped by
 * its owning DynamicForm step and returned in document order. Steps that
 * contribute no eligible field are omitted entirely, so an empty result means
 * "there is nothing this callsite could ever gate on".
 *
 * This is the single source of truth for picker scope: the modal renders it as
 * cascading Step → Field selects, and {@link hasEligibleSourceFields} asks it
 * whether the "add condition" affordance should be offered at all. Keeping both
 * on one walk is deliberate — they used to be separate hand-maintained copies,
 * and any drift showed up as a disabled button sitting over a non-empty picker.
 */
export function eligibleSourceFields(
  allSteps: WorkflowStep[],
  scope: ConditionScope,
  isFr: boolean,
): StepWithFields[] {
  const ownStepIndex =
    scope.kind === 'pdf' ? -1 : allSteps.findIndex((s) => s.id === scope.ownStepId);

  const out: StepWithFields[] = [];
  for (let i = 0; i < allSteps.length; i++) {
    const s = allSteps[i]!;
    if (s.type !== 'DynamicForm') continue;

    // Step-level scope: only PRIOR steps are eligible sources.
    if (scope.kind === 'step' && (ownStepIndex < 0 || i >= ownStepIndex)) continue;
    // Field- and section-level scope: prior steps contribute every eligible
    // field; the owning step is narrowed further by the in-step bounds below.
    // Steps AFTER the owning one are out of reach for both — a field can only
    // gate on something the advisor has already been asked.
    if (scope.kind === 'field' || scope.kind === 'section') {
      if (ownStepIndex < 0) continue;
      if (i > ownStepIndex) continue;
    }
    const isOwningStep = scope.kind !== 'pdf' && s.id === scope.ownStepId;

    const fields: WorkflowField[] = [];
    for (let secIdx = 0; secIdx < s.sections.length; secIdx++) {
      const sec = s.sections[secIdx]!;

      // Section-level scope, inside the OWNING step only: exclude every
      // field inside ownSection AND any section that comes at-or-after it.
      // Prior steps are unaffected — all their sections are eligible.
      if (scope.kind === 'section' && isOwningStep) {
        if (sec.id === scope.ownSectionId) continue;
        const ownSecIdx = s.sections.findIndex((x) => x.id === scope.ownSectionId);
        if (ownSecIdx >= 0 && secIdx >= ownSecIdx) continue;
      }

      for (let rowIdx = 0; rowIdx < sec.rows.length; rowIdx++) {
        const row = sec.rows[rowIdx]!;
        for (let colIdx = 0; colIdx < row.columns.length; colIdx++) {
          const col = row.columns[colIdx]!;
          if (!col.field || !CONDITION_SOURCE_TYPES.has(col.field.type)) continue;

          // Field-level scope, inside the OWNING step only: keep fields
          // strictly before the field-under-edit in document order. Same
          // section: row before, or same row and column before. Different
          // section: any earlier section. Prior steps skip this entirely —
          // their sections/rows carry no ordering relationship to the
          // field-under-edit, and comparing against ownSectionId there would
          // fall through to `ownSecIdx < 0` and silently drop every field.
          if (scope.kind === 'field' && isOwningStep) {
            if (sec.id === scope.ownSectionId) {
              // Same section. Bound by (rowIdx, colIdx) lex order vs own.
              const ownRowIdx = sec.rows.findIndex((r) => r.id === scope.ownRowId);
              if (ownRowIdx < 0) continue;
              if (rowIdx > ownRowIdx) continue;
              if (rowIdx === ownRowIdx) {
                const ownColIdx = sec.rows[ownRowIdx]!.columns.findIndex(
                  (c) => c.id === scope.ownColumnId,
                );
                if (ownColIdx < 0) continue;
                if (colIdx >= ownColIdx) continue;
              }
            } else {
              // Different section — must come BEFORE the field's section.
              const ownSecIdx = s.sections.findIndex((x) => x.id === scope.ownSectionId);
              if (ownSecIdx < 0) continue;
              if (secIdx >= ownSecIdx) continue;
            }
          }
          fields.push(col.field);
        }
      }
    }
    if (fields.length === 0) continue;
    const stepTitle = (isFr ? s.titleFr : s.titleEn) || s.titleEn || s.titleFr;
    out.push({ stepId: s.id, stepTitle, fields });
  }
  return out;
}

/**
 * True when at least one selectable source field is reachable for this scope.
 * Callers use this to hide the "add condition" button entirely when there's
 * nothing the user could ever pick — better than disabling it with no
 * explanation. Labels are irrelevant to the answer, so locale is arbitrary.
 */
export function hasEligibleSourceFields(allSteps: WorkflowStep[], scope: ConditionScope): boolean {
  return eligibleSourceFields(allSteps, scope, false).length > 0;
}
